import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Box, CircularProgress, Typography, Button } from '@mui/material';
import RoleGuard from './RoleGuard';

function ProtectedRoute({ 
  children, 
  requireAuth = true, 
  allowedRoles = [],
  fallbackComponent = null 
}) {
  const { user, isAuthenticated, loading } = useAuth();
  const location = useLocation();

  if (loading) {
    return (
      <Box 
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100vh',
          gap: 2
        }}
      >
        <CircularProgress />
        <Typography variant="h6" color="primary">
          Authenticating...
        </Typography>
      </Box>
    );
  }

  if (requireAuth && (!user || !isAuthenticated)) {
    // Redirect to login page with the attempted location
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (!requireAuth && user && isAuthenticated) {
    // Redirect to the page they originally tried to access, or dashboard
    const from = location.state?.from?.pathname;
    const destination = (from && from !== '/login') ? from : '/dashboard';
    return <Navigate to={destination} replace />;
  }

  // If authenticated and roles are specified, use RoleGuard
  if (requireAuth && allowedRoles.length > 0) {
    return (
      <RoleGuard allowedRoles={allowedRoles} fallbackComponent={fallbackComponent}>
        {children}
      </RoleGuard>
    );
  }

  return children;
}

export default ProtectedRoute;